/// <reference path="../../../local-typings/cast.d.ts" />

import { ElementRef, NgZone } from '@angular/core';
import { PlaybackService, PlaybackSession, AssetResolution, HTMLMediaPlaybackSession, PlaybackHints, GoogleCastService, PlaybackItem, PlayState, TYTCastMediaLoadData, TYTCastLiveMediaLoadData, TYTCastVODMediaLoadData } from 'media-playback';
import { Injectable } from '@angular/core';
import { AvAsset, LiveStream } from 'models';
import { Subject, Observable } from 'rxjs';

//import * as Hls from 'hls.js';

declare let Hls: any; // TODO: fix this after hls.js can support SSR

export class CastRemotePlaybackSession implements PlaybackSession {
    constructor(
        public ngZone : NgZone,
        public service : CastRemotePlaybackService,
        public castService : GoogleCastService,
        public asset : AvAsset,
        private _element : HTMLElement,
        private playbackItem : PlaybackItem,
        private deviceNameElement : HTMLElement,
        private initialPosition : number
    ) {
        this.position = initialPosition;
        let registeredMediaSession : chrome.cast.media.Media = null;

        this.remotePlayerListener = ev => {
            try {
                //console.debug(`GoogleCast/rplayer: ${ev.field}='${ev.value}'`);
                this.ngZone.runGuarded(() => this.updateState());
            } catch (e) {
                console.error(`Caught error during updateState():`);
                console.error(e);
            }
        };

        this.createRemotePlayer();

        this.updateDeviceName();

        castService.currentSession.addEventListener(cast.framework.SessionEventType.MEDIA_SESSION, ev => {
            console.log(`CastRemote/PlaybackSession: New media session found`);
            
            this.updateDeviceName();

            let newSession = ev.mediaSession;

            if (registeredMediaSession === newSession) {
                console.warn(`GoogleCast/CastRemotePlaybackSession: Received new media session, but the session did not change. Skipping attach.`);
                return;
            }

            console.log(`GoogleCast/CastRemotePlaybackSession: Received new media session`);

            registeredMediaSession = newSession;

            // registeredMediaSession.addUpdateListener(isAlive => {
            //     console.debug(`GoogleCast/CastRemotePlaybackSession: Received update event, isAlive=${isAlive}`);
            // });

            //window['rms'] = registeredMediaSession;                

            this.destroyRemotePlayer();
            this.createRemotePlayer();
        });

        this.updateInterval = setInterval(() => this.ngZone.runGuarded(() => this.updateState()), 1000);
    }

    readonly enableSeekOnStart = false;
    readonly pauseOnDismiss = false;
    readonly pauseOnTakeOver = false;
    readonly pauseOnDestroy = false;
    readonly videoDisabled = true;
    
    get playState() {
        return this._playState;
    }
    
    updateDeviceName() {
        if (!this.castService.currentSession)
            return;

        let device = this.castService.currentSession.getCastDevice();

        if (!device)
            return;

        this.deviceNameElement.innerText = device.friendlyName;
    }

    get item() {
        return this.playbackItem;
    }

    get castHasMedia() {
        if (!this.castService.currentSession)
            return false;

        let mediaSession = this.castService.currentSession.getMediaSession();

        if (!mediaSession || !mediaSession.media)
            return false;

        if (!mediaSession.media.contentId)
            return false;

        return true;
    }

    readonly isCastRemoteSession : boolean = true;

    get isCurrentMedia() {
        if (!this.castService.currentSession)
            return false;

        let mediaSession = this.castService.currentSession.getMediaSession();

        if (!mediaSession || !mediaSession.media)
            return false;

        let loadData : TYTCastMediaLoadData;
        
        try {
            loadData = JSON.parse(atob(mediaSession.media.contentId));
        } catch (e) {
            console.error(`Failed to parse cast content ID '${mediaSession.media.contentId}'`);
            return false;
        }

        let currentItemID : string;
        let expectedItemID : string;

        if (loadData.type === 'live') {
            let liveLoadData = <TYTCastLiveMediaLoadData>loadData;
            currentItemID = ''+liveLoadData.liveStreamID;
        } else {
            let vodLoadData = <TYTCastVODMediaLoadData>loadData;
            currentItemID = vodLoadData.playlistItemID;
        }

        expectedItemID = ''+this.playbackItem.item.id;
        
        return currentItemID == expectedItemID;
    }

    private rplayer : cast.framework.RemotePlayer;
    private rplayerController : cast.framework.RemotePlayerController;

    private updateInterval;

    private createRemotePlayer() {
        this.rplayer = new cast.framework.RemotePlayer();
        this.rplayerController = new cast.framework.RemotePlayerController(this.rplayer);
        this.rplayerController.addEventListener(cast.framework.RemotePlayerEventType.ANY_CHANGE, this.remotePlayerListener);
    }

    private remotePlayerListener;

    private destroyRemotePlayer() {
        if (this.rplayerController)
            this.rplayerController.removeEventListener(cast.framework.RemotePlayerEventType.ANY_CHANGE, this.remotePlayerListener);

        this.rplayer = null;
        this.rplayerController = null;
    }

    private updateState() {
        let castSession = this.castService.currentSession;
        if (!castSession)
            return;
        let mediaSession = castSession.getMediaSession();
        if (!mediaSession)
            return;

        if (!this.isCurrentMedia)
            return;

        if (this.rplayer.duration == 0) {
            this.destroyRemotePlayer();
            this.createRemotePlayer();
        }

        this.position = this.rplayer.currentTime;
        //this.position = mediaSession.getEstimatedTime();

        this.length = this.rplayer.duration;
        //this.length = mediaSession.media.duration;

        this._positionChanged.next(this.position);
        this._lengthChanged.next(this.length);

        let state : PlayState = 'unstarted';
        let castState : chrome.cast.media.PlayerState;
        
        castState = this.rplayer.playerState;
        //castState = mediaSession.playerState;

        if (castState == chrome.cast.media.PlayerState.IDLE)
            state = 'unstarted';

        // WOW. Cast SDK _ALWAYS_ reports buffering. ALWAYS.
        // THERE IS NO WAY TO STOP IT.
        // The media is NOT buffering on the cast device, but 
        // it don't give a fuck. We MUST report PLAYING state 
        // when cast indicates buffering or we will FOREVER SHOW 
        // THE CAST REMOTE BUFFERING.

        else if (castState == chrome.cast.media.PlayerState.BUFFERING)
            state = 'playing'; // NOT BUFFERING
        else if (castState == chrome.cast.media.PlayerState.PAUSED)
            state = 'paused';
        else if (castState == chrome.cast.media.PlayerState.PLAYING)
            state = 'playing';

        this._playState = state;
        this._playStateChanged.next(state);

        if (window['debug'])
            console.debug(`GoogleCast/Remote: Playback status update: position=${this.position}, length=${this.length}, state=${state}`);
    }

    private _playState : PlayState = 'unstarted';

    public get element() {
        return { nativeElement: this._element };
    }

    readonly isGoogleCastSession : boolean = true;

    _positionChanged : Subject<number> = new Subject<number>();
    _lengthChanged : Subject<number> = new Subject<number>();
    _finished : Subject<void> = new Subject<void>();
    _playStateChanged : Subject<PlayState> = new Subject<PlayState>();
    _volumeChanged : Subject<number> = new Subject<number>();
    _onSeek : Subject<number> = new Subject<number>();

    position: number;
    length: number;
    hasChat: boolean;
    chatEmbed: string;
    seekable?: boolean;
    popOutURL?: string;
    volume: number;
    started: boolean = false;

    get onSeek(): Observable<number> {
        return this._onSeek;
    }

    get positionChanged(): Observable<number> {
        return this._positionChanged;
    }

    get lengthChanged(): Observable<number> {
        return this._lengthChanged;
    }

    get finished(): Observable<void> {
        return this._finished;
    };

    get playStateChanged(): Observable<PlayState> {
        return this._playStateChanged;
    }

    get volumeChanged(): Observable<number> {
        return this._volumeChanged;
    }

    async pause(): Promise<void> {
        if (!this.castService.currentSession)
            return;
        
        if (!this.isCurrentMedia)
            return;

        let session = this.castService.currentSession.getMediaSession();

        if (!session)
            return;
        
        try {
            await new Promise<void>((resolve, reject) => {
                session.pause(
                    new chrome.cast.media.PauseRequest(), 
                    () => resolve(), 
                    err => reject(err)
                );
            });
        } catch (e) {
            console.error(`GoogleCast/RemotePlaybackSession: Caught error while sending pause() message to Google Cast:`);
            console.error(e);
        }
        this.updateState();
    }

    async resume(): Promise<void> {

        if (!this.castService.currentSession)
            return;
        
        let session = this.castService.currentSession.getMediaSession();

        console.log(`Resume.... invalidSession=${!session}, isCurrentMedia=${this.isCurrentMedia}`);
        if (!this.isCurrentMedia || !session) {
            console.log('Not the current media...');

            console.log('Cast is ready...');
            // We need to change media on the cast device...
            console.log(`GoogleCast/Remote: Loading new media onto Cast device...`);
            console.dir(this.playbackItem);

            if (this.playbackItem.type === 'stream') {
                this.castService.playLiveStream(
                    Number(this.playbackItem.item.id)
                );
            } else {
                this.castService.playVOD(
                    this.playbackItem.production.id, 
                    this.playbackItem.playlistID, 
                    <string>this.playbackItem.item.id, 
                    this.position
                );
            }
        }
    
        if (!session)
            return;
        
        if (!this.castHasMedia) {
            console.log(`GoogleCast/Remote: Received play() but Cast is not ready for it yet!`);
            return;
        }

        try {
            await new Promise<void>((resolve, reject) => {
                session.play(
                    new chrome.cast.media.PlayRequest(), 
                    () => resolve(), 
                    err => reject(err)
                );
            });
        } catch (e) {
            console.error(`GoogleCast/RemotePlaybackSession: Caught error while sending play() message to Google Cast:`);
            console.error(e);
        }
        this.updateState();
    }

    async setVolume(level: number) {
        if (!this.castService.currentSession)
            return;
        
        if (!this.isCurrentMedia)
            return;

        let session = this.castService.currentSession.getMediaSession();

        if (!session)
            return;
        
        try {
            await new Promise<void>((resolve, reject) => {
                session.setVolume(
                    new chrome.cast.media.VolumeRequest(new chrome.cast.Volume(level, false)), 
                    () => resolve(), 
                    err => reject(err)
                );
            });
        } catch (e) {
            console.error(`GoogleCast/RemotePlaybackSession: Caught error while sending setVolume() message to Google Cast:`);
            console.error(e);
        }
    }

    async seek(position: number) {
        if (!this.castService.currentSession)
            return;
        
        if (!this.isCurrentMedia)
            return;

        let session = this.castService.currentSession.getMediaSession();

        if (!session)
            return;
        
        this._onSeek.next(position);

        let seekRequest = new chrome.cast.media.SeekRequest();
        seekRequest.currentTime = position;
        seekRequest.resumeState = chrome.cast.media.ResumeState.PLAYBACK_START;
        
        try {
            await new Promise<void>((resolve, reject) => {
                session.seek(
                    seekRequest, 
                    () => resolve(), 
                    err => reject(err)
                );
            });
        } catch (e) {
            console.error(`GoogleCast/RemotePlaybackSession: Caught error while sending seek() message to Google Cast:`);
            console.error(e);
        }
    }

    destroy() {
        clearInterval(this.updateInterval);
        this._element.remove();
    }
}

/**
 * HLS playback service
 */
@Injectable()
export class CastRemotePlaybackService implements PlaybackService {
    constructor(
        private ngZone : NgZone,
        private castService : GoogleCastService
    ) {
    }

    /**
     * Priority of this playback service amongst the registered playback services.
     * Regular services have a priority of zero, and TYT.com tries services with a 
     * higher priority number before services with a lower priority number.
     */
    get priority() {
        return 10;
    }

    get id() {
        return 'cast-remote';
    }

    resolve(item: PlaybackItem): AssetResolution {
        let asset : AvAsset = item.item.asset;

        if (!this.castService.currentSession)
            return null;

        // Accept all media, we will forward it to the connected Google Cast device

        return {
            item,
            service: this,
            asset,
            data: {
                item
            }
        };
    }

    /**
     * Play the given media resolution within the given root element.
     */
    async play(mediaResolution: AssetResolution, rootElement: ElementRef, hints?: PlaybackHints): Promise<PlaybackSession> {
        if (!hints)
            hints = {};

        console.log(`GoogleCast/Remote: Starting session for media:`);
        console.dir(mediaResolution);

        let item : PlaybackItem = mediaResolution.data.item;

        let containerElement : HTMLElement = rootElement.nativeElement;

        let castElement : HTMLElement = document.createElement('div');
        castElement.classList.add('remote-cast-player');

        castElement.innerHTML = `
            <div class="cast-playback-element">
                <div style="flex-grow: 1;"></div>
                <div>
                    <img src="assets/cast/connected.png" style="height: auto;" />
                </div>
                <div>
                    Casting to <strong><span class="device-name"></span></strong>
                </div>
            </div>
        `;

        containerElement.appendChild(castElement);

        let deviceNameElement : HTMLElement = castElement.getElementsByClassName('device-name')[0] as any;

        let castContext = cast.framework.CastContext.getInstance();
        let castSession = castContext.getCurrentSession();
        let mediaSession = castSession.getMediaSession();

        if (mediaSession) {
            console.log(`GoogleCast/Remote: Existing media session in progress`);
        } else {
            // if (item.type == 'stream') {
            //     let stream = <LiveStream>item.item;
            //     // TODO: LiveStream.id is a number, not a string
            //     this.castService.playLiveStream(<any>stream.id);
            // } else {
            //     console.log(`GoogleCast/Remote: Starting VOD session:`);
            //     console.dir({
            //         productionID: item.production.id, 
            //         playlistID: item.playlistID, 
            //         itemID: item.item.id, 
            //         position: hints.startPosition || 0
            //     });
            //     this.castService.playVOD(item.production.id, item.playlistID, item.item.id, hints.startPosition || 0);
            // }
        }
        
        return new CastRemotePlaybackSession(
            this.ngZone, 
            this, 
            this.castService, 
            mediaResolution.asset, 
            castElement, 
            item, 
            deviceNameElement, 
            hints.startPosition || 0
        );
    }
}
